/**
 * Pure presentation/telemetry helpers for edge bots — the queue-distance heat
 * ramp, posture tone, queue math, climb/ETA motion derivation, and formatters.
 * Extracted from the Recycler page so the Position Tracker bot zones reuse the
 * exact same intelligence + visual vocabulary. No React, no I/O.
 */
import type { AgentStats } from './edge';

export const FRONT_AT = 20; // <= contracts ahead → at the front (fill / adverse-selection risk)
export const FRONT_NEAR = 50; // <= contracts ahead → near the front
export const QUEUE_SCALE = 300; // contracts reference for the fill anchor (full = at the front)
export const POLL_MS = 1000;
export const HIST = 24; // poll samples kept per agent for climb / ETA / sparkline

export type ViewMode = 'compact' | 'card' | 'table';
export type SortKey = 'front' | 'eta' | 'net' | 'errors';
export type Sample = { ms: number; front: number };
export type Motion = { climb: number; eta: number; spark: number[] };

// ── queue-distance heat: the ONLY rainbow axis. RED far from the front → GREEN
//    as the line nears fill. `rgb` is the raw triplet for full-row gradients.
export function heat(ahead: number): { text: string; fill: string; bar: string; rgb: string } {
  if (ahead < 0) return { text: 'text-muted-foreground', fill: 'bg-muted-foreground/10', bar: 'bg-muted-foreground/50', rgb: '148 163 184' };
  if (ahead <= FRONT_AT) return { text: 'text-green-400', fill: 'bg-green-500/12', bar: 'bg-green-500', rgb: '34 197 94' };
  if (ahead <= FRONT_NEAR) return { text: 'text-amber-400', fill: 'bg-amber-500/12', bar: 'bg-amber-500', rgb: '245 158 11' };
  return { text: 'text-red-400', fill: 'bg-red-500/12', bar: 'bg-red-500', rgb: '239 68 68' };
}

// ── posture: bot health, a SEPARATE channel (dot + pill, never the heat ramp).
export function postureTone(p: string): { dot: string; cls: string; label: string } {
  switch (p) {
    case 'green': return { dot: 'bg-green-500', cls: 'text-green-400', label: 'healthy' };
    case 'yellow': return { dot: 'bg-amber-500', cls: 'text-amber-400', label: 'working' };
    case 'orange': return { dot: 'bg-orange-500', cls: 'text-orange-400', label: 'strained' };
    case 'red': return { dot: 'bg-red-500', cls: 'text-red-400', label: 'trouble' };
    default: return { dot: 'bg-muted-foreground', cls: 'text-muted-foreground', label: p || '—' };
  }
}

export function frontOf(a: AgentStats): number {
  const r = (a.resting ?? []).map((o) => o.queuePosition).filter((q) => q >= 0);
  return r.length ? Math.min(...r) : -1;
}

/** Whether to surface the front-of-book metrics (front / climb / fill-ETA — all
 *  derived from queuePosition) for this bot RIGHT NOW. DATA-driven, not kind-
 *  driven: the edge enriches queuePosition for every resting kind (incl. vanilla
 *  MM, via the g10edge runner depth enrichment), so we show the cells whenever a
 *  live queue position is present and fall back to "—" (feed down / between
 *  cycles / no resting leg) when it isn't. The earlier /recycl/ kind gate WRONGLY
 *  hid a live MM bot's real queue position — the edge had been emitting it all
 *  along — which is exactly the visibility Josh wanted on the running MM bot. */
export function hasLiveQueue(a: AgentStats): boolean {
  return frontOf(a) >= 0;
}

export function depthSource(a: AgentStats): string {
  const direct = (a.depthSource ?? '').trim();
  if (direct) return direct;
  const sources = new Set((a.resting ?? []).filter((o) => o.queuePosition >= 0).map((o) => o.queueSource).filter(Boolean));
  if (sources.size === 0) return 'none';
  if (sources.size > 1) return 'mixed';
  return sources.values().next().value ?? 'none';
}

export function sourceTone(source: string): string {
  switch (source) {
    case 'direct': return 'text-green-400';
    case 'hydra': return 'text-amber-400';
    case 'mixed': return 'text-sky-400';
    case 'orders': return 'text-muted-foreground';
    default: return 'text-muted-foreground/50';
  }
}

export function queueState(a: AgentStats): { label: string; cls: string } {
  if (a.haltReason) {
    return { label: queueReasonLabel(a.haltReason), cls: 'text-red-400' };
  }
  if (hasLiveQueue(a)) {
    const src = depthSource(a);
    return { label: src === 'none' ? 'live' : src, cls: sourceTone(src) };
  }
  switch (a.queueStatus) {
    case 'suppressed':
      return { label: queueReasonLabel(a.queueStatusReason), cls: 'text-amber-400' };
    case 'unknown_queue':
      return { label: 'unknown', cls: 'text-muted-foreground/60' };
    case 'placing':
      return { label: 'placing', cls: 'text-sky-400' };
    case 'waiting_for_market':
      return { label: 'waiting', cls: 'text-muted-foreground/60' };
    case 'halted':
      return { label: queueReasonLabel(a.queueStatusReason), cls: 'text-red-400' };
    default:
      return { label: a.queueStatus || 'idle', cls: 'text-muted-foreground/50' };
  }
}

export function queueReasonLabel(reason?: string): string {
  switch (reason) {
    case 'no_depth': return 'no depth';
    case 'too_shallow': return 'too shallow';
    case 'unknown_queue': return 'unknown';
    case 'partial_depth': return 'partial';
    case 'book_guard': return 'book guard';
    case 'filled_inventory': return 'filled halt';
    case 'no_view': return 'no book';
    case 'safe_queue': return 'safe';
    case 'no_resting': return 'idle';
    default: return reason || 'idle';
  }
}

export function restingTotal(a: AgentStats): number {
  return (a.resting ?? []).reduce((s, o) => s + o.sizeContracts, 0);
}
export function progressPct(front: number): number {
  if (front < 0) return 0;
  return Math.max(2, Math.min(100, Math.round((1 - Math.min(front, QUEUE_SCALE) / QUEUE_SCALE) * 100)));
}
export function legsOf(a: AgentStats): { side: 'bid' | 'ask'; size: number; price: number }[] {
  const r = a.resting ?? [];
  const out: { side: 'bid' | 'ask'; size: number; price: number }[] = [];
  for (const side of ['bid', 'ask'] as const) {
    const os = r.filter((o) => o.side === side);
    if (os.length) out.push({ side, size: os.reduce((x, o) => x + o.sizeContracts, 0), price: os[0].priceCents });
  }
  return out;
}
export function uptime(startedAtMs: number): string {
  if (!startedAtMs) return '—';
  const s = Math.max(0, Math.round((Date.now() - startedAtMs) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60)}m`;
}

export function deriveMotion(hist: Sample[] | undefined): Motion {
  const all = hist ?? [];
  const valid = all.filter((s) => s.front >= 0);
  const spark = valid.map((s) => Math.max(0, QUEUE_SCALE - Math.min(s.front, QUEUE_SCALE))); // rising = climbing
  // climb/ETA over ONLY the trailing contiguous run of known-front samples, so a
  // re-quote (front → unknown(-1) → new front) isn't bridged into a bogus
  // derivative. MM bots cancel/replace constantly, making that gap common; a
  // bridged climb would over-state how fast the line is nearing the front (and
  // under-state the fill-ETA). < 2 contiguous samples ⇒ no climb yet (honest "—").
  const run: Sample[] = [];
  for (let i = all.length - 1; i >= 0 && all[i].front >= 0; i--) run.unshift(all[i]);
  if (run.length < 2) return { climb: 0, eta: -1, spark };
  const a = run[0];
  const b = run[run.length - 1];
  const dt = (b.ms - a.ms) / 1000;
  const climb = dt > 0 ? (a.front - b.front) / dt : 0; // contracts/sec advancing toward the front
  const eta = climb > 0.02 && b.front >= 0 ? b.front / climb : -1;
  return { climb, eta, spark };
}

export const fmtClimb = (c: number) => (Math.abs(c) < 0.05 ? '0/s' : `${c > 0 ? '+' : ''}${c.toFixed(1)}/s`);
export const fmtEta = (e: number) => (e < 0 ? '—' : e < 90 ? `~${Math.round(e)}s` : `~${Math.round(e / 60)}m`);
export const fmtVel = (v?: number) => (v == null ? '—' : `${v.toFixed(1)}¢/s`);
export const fmtNet = (n: number) => `${n > 0 ? '+' : ''}${n}`;
export function markout5s(a: AgentStats): string {
  const m = (a.markout ?? []).find((x) => x.horizonMs === 5000);
  return m && m.fills > 0 ? `${Math.round(m.rightSideRate * 100)}%` : '—';
}
